// Radha — deduction / short-pay desk (073).
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { supabase } from "../lib/supabase";

export type DeductionStatus = "open" | "researching" | "valid" | "disputed" | "recovered" | "written_off";

export interface Deduction {
  id: string; invoice_id: string | null; customer_name: string;
  amount: string; currency: string; reason_code: string;
  claim_reference: string | null; status: DeductionStatus;
  resolved_amount: string | null; identified_date: string; resolved_date: string | null;
  notes: string | null;
}

export interface DeductionSummaryRow {
  reason_code: string; label: string; category: string; usually_valid: boolean;
  open_count: number; open_amount: string;
  resolved_count: number; recovered_amount: string; written_off_amount: string;
  recovery_rate: string | null;
}

export interface ReasonCode {
  code: string; label: string; category: string; usually_valid: boolean; guidance: string | null;
}

export function useDeductions() {
  return useQuery({
    queryKey: ["deductions"],
    queryFn: async (): Promise<Deduction[]> => {
      const { data, error } = await supabase
        .from("deductions")
        .select("id, invoice_id, customer_name, amount, currency, reason_code, claim_reference, status, resolved_amount, identified_date, resolved_date, notes")
        .is("deleted_at", null)
        .order("identified_date", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 20_000,
  });
}

/** Per-reason-code exposure and recovery rate — how a deductions desk is run. */
export function useDeductionSummary() {
  return useQuery({
    queryKey: ["deduction-summary"],
    queryFn: async (): Promise<DeductionSummaryRow[]> => {
      const { data, error } = await supabase.rpc("deduction_summary");
      if (error) throw error;
      return (data as DeductionSummaryRow[]) ?? [];
    },
    staleTime: 30_000,
  });
}

export function useReasonCodes() {
  return useQuery({
    queryKey: ["deduction-reason-codes"],
    queryFn: async (): Promise<ReasonCode[]> => {
      const { data, error } = await supabase
        .from("deduction_reason_codes")
        .select("code, label, category, usually_valid, guidance")
        .order("category", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 600_000,
  });
}

export function useResolveDeduction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status, resolved_amount }: { id: string; status: DeductionStatus; resolved_amount?: number }) => {
      // resolved_date is required by a CHECK exactly when the status is
      // terminal, so it is derived here rather than left to the caller.
      const terminal = status === "recovered" || status === "written_off";
      const patch: Record<string, unknown> = {
        status,
        resolved_date: terminal ? new Date().toISOString().slice(0, 10) : null,
        resolved_amount: terminal ? (status === "recovered" ? resolved_amount ?? 0 : 0) : null,
      };
      const { error } = await supabase.from("deductions").update(patch).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deductions"] });
      qc.invalidateQueries({ queryKey: ["deduction-summary"] });
    },
  });
}

export function deductionErrorMessage(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  const m = msg.match(/^([A-Z_]+):\s*(.*)$/);
  return m ? m[2] || m[1] : msg;
}
